import type { PlainDate, TagColor } from "@mapcraft-tasks/domain";
import { createContext, type ReactNode, useContext, useMemo, useSyncExternalStore } from "react";
import * as select from "./selectors";
import type { Store, StoreSnapshot } from "./store";
import type { Command, CommandLocal, ConflictRecord, Node, SyncStatus } from "./types";

/**
 * React-биндинги — ТОНКИЙ слой над фреймворк-независимым стором.
 *
 * Всё, что импортируют экраны, — здесь. Ни `fetch`, ни tRPC, ни `navigator.onLine`
 * в коде экранов нет и быть не должно (offline-sync §3).
 */

const StoreContext = createContext<Store | null>(null);

export function StoreProvider({ store, children }: { store: Store; children: ReactNode }) {
	return <StoreContext.Provider value={store}>{children}</StoreContext.Provider>;
}

export function useStore(): Store {
	const store = useContext(StoreContext);
	if (!store) {
		throw new Error("StoreProvider не смонтирован");
	}
	return store;
}

function useSnapshot(): StoreSnapshot {
	const store = useStore();
	return useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
}

/**
 * Подписка на реплику целиком. Нужна тем, кто считает свою производную от дерева
 * (аутлайнер строит плоский список видимых строк) и не укладывается в готовый селектор.
 * Брать `store.getSnapshot()` напрямую нельзя: без подписки экран замрёт на первом снимке.
 */
export function useReplica(): StoreSnapshot["replica"] {
	return useSnapshot().replica;
}

/** Запись — одна дверь. Синхронна и не возвращает промис: ждать нечего. */
export function useDispatch(): (command: Command, local?: CommandLocal) => void {
	return useStore().dispatch;
}

export function useNode(id: string | undefined): Node | undefined {
	const snapshot = useSnapshot();
	return useMemo(() => (id ? select.getNode(snapshot.replica, id) : undefined), [snapshot, id]);
}

export function useChildren(containerId: string): select.OutlineEntry<Node>[] {
	const snapshot = useSnapshot();
	return useMemo(() => select.children(snapshot.replica, containerId), [snapshot, containerId]);
}

export function useDayPage(date: PlainDate): select.OutlineEntry<Node>[] {
	const snapshot = useSnapshot();
	return useMemo(() => select.dayPage(snapshot.replica, date), [snapshot, date]);
}

export function useScheduledOn(date: PlainDate): Node[] {
	const snapshot = useSnapshot();
	return useMemo(() => select.scheduledOn(snapshot.replica, date), [snapshot, date]);
}

export function useRecurringOn(date: PlainDate): select.RecurringEntry<Node>[] {
	const snapshot = useSnapshot();
	return useMemo(() => select.recurringOn(snapshot.replica, date), [snapshot, date]);
}

export function useInbox(): select.OutlineEntry<Node>[] {
	const snapshot = useSnapshot();
	return useMemo(() => select.inbox(snapshot.replica), [snapshot]);
}

export function usePinned(): select.OutlineEntry<Node>[] {
	const snapshot = useSnapshot();
	return useMemo(() => select.pinned(snapshot.replica), [snapshot]);
}

export function useActiveFocuses(): Node[] {
	const snapshot = useSnapshot();
	return useMemo(() => select.activeFocuses(snapshot.replica), [snapshot]);
}

export function useFocuses(): Node[] {
	const snapshot = useSnapshot();
	return useMemo(() => select.focuses(snapshot.replica), [snapshot]);
}

export function useProjects(): Node[] {
	const snapshot = useSnapshot();
	return useMemo(() => select.projects(snapshot.replica), [snapshot]);
}

export function useOverdue(): select.OverdueEntry<Node>[] {
	const snapshot = useSnapshot();
	const today = useToday();
	return useMemo(() => select.overdue(snapshot.replica, today), [snapshot, today]);
}

export function useBacklinks(nodeId: string): Node[] {
	const snapshot = useSnapshot();
	return useMemo(() => select.backlinks(snapshot.replica, nodeId), [snapshot, nodeId]);
}

export function useAreas(): Node[] {
	const snapshot = useSnapshot();
	return useMemo(() => select.areas(snapshot.replica), [snapshot]);
}

export function useDays(): Node[] {
	const snapshot = useSnapshot();
	return useMemo(() => select.days(snapshot.replica), [snapshot]);
}

export function useAllRecurring(): Node[] {
	const snapshot = useSnapshot();
	return useMemo(() => select.allRecurring(snapshot.replica), [snapshot]);
}

export function useSearch(query: string): Node[] {
	const snapshot = useSnapshot();
	return useMemo(() => select.search(snapshot.replica, query), [snapshot, query]);
}

export function useAllTags(): { tag: string; count: number }[] {
	const snapshot = useSnapshot();
	return useMemo(() => select.allTags(snapshot.replica), [snapshot]);
}

/**
 * Карта цветов тегов. Ссылка стабильна, пока реплика не изменилась, — строки аутлайна
 * берут её все разом, и пересчитывать её на каждую строку было бы лишним.
 */
export function useTagColors(): ReadonlyMap<string, TagColor> {
	const snapshot = useSnapshot();
	return useMemo(() => select.tagColors(snapshot.replica), [snapshot]);
}

export function useBreadcrumbs(nodeId: string): Node[] {
	const snapshot = useSnapshot();
	return useMemo(() => select.breadcrumbs(snapshot.replica, nodeId), [snapshot, nodeId]);
}

export function usePlacementsOf(nodeId: string): { placementId: string; container: Node }[] {
	const snapshot = useSnapshot();
	return useMemo(() => select.placementsOf(snapshot.replica, nodeId), [snapshot, nodeId]);
}

export function useIsPinned(nodeId: string): boolean {
	const snapshot = useSnapshot();
	return useMemo(() => select.isPinned(snapshot.replica, nodeId), [snapshot, nodeId]);
}

export function useHasChildren(containerId: string): boolean {
	const snapshot = useSnapshot();
	return useMemo(() => select.hasChildren(snapshot.replica, containerId), [snapshot, containerId]);
}

/** Служебное — для индикаторов, НЕ для логики экранов. */
export function useSyncStatus(): SyncStatus {
	return useSnapshot().status;
}

export function useConflicts(): ConflictRecord[] {
	return useSnapshot().conflicts;
}

/**
 * Ноды, по которым висит неразобранный конфликт. На них рисуется ПОСТОЯННЫЙ `⚠` —
 * вспышку можно пропустить, поэтому она никогда не единственный носитель
 * (ui-spec Область 11).
 */
export function useConflictedNodes(): ReadonlySet<string> {
	const snapshot = useSnapshot();
	return useMemo(() => new Set(snapshot.conflicts.flatMap((c) => c.nodeIds)), [snapshot]);
}

/** Таймзона системы — из настройки в базе. Часы браузера не используются нигде. */
export function useTimezone(): string {
	return useSnapshot().tz;
}

export function useToday(): PlainDate {
	const store = useStore();
	// Дата пересчитывается на каждый снимок: за полночь «сегодня» обязано смениться само.
	useSnapshot();
	return store.today();
}
